import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "../../Reveal";
import { useI18n } from "../../../i18n/LanguageContext";
import { SITE } from "../../../content/site";
import { routePath } from "../../../lib/routes";
import { track, EVENTS } from "../../../lib/analytics";

export default function EventsCredentials() {
  const { lang } = useI18n();
  const hs = SITE[lang].homeStory || SITE.en.homeStory;
  const ev = SITE[lang].events || SITE.en.events;
  const c = hs.credentials;

  return (
    <>
      {/* Editorial timeline */}
      <section data-testid="home-credentials" className="bg-white px-6 py-28 md:px-12 md:py-40">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <Reveal><p className="overline text-[#0e7c6b]">{c.overline}</p></Reveal>
                <Reveal delay={0.1}>
                  <h2 className="font-display mt-7 text-3xl font-light leading-[1.05] tracking-tightest text-[#050505] md:text-5xl">{c.title}</h2>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#4a4a4a] md:text-lg">{c.text}</p>
                </Reveal>
                <Reveal delay={0.3}>
                  <img src="/events/press.jpg" alt={ev.alt.formats} loading="lazy" width="1600" height="1067"
                    className="mt-12 hidden aspect-[4/3] w-full rounded-2xl object-cover lg:block" />
                </Reveal>
              </div>
            </div>

            <div className="lg:col-span-7 lg:col-start-6">
              <div className="relative border-l border-black/12 pl-10 md:pl-14">
                {ev.experience.items.map((it, i) => (
                  <Reveal key={i} delay={0.1 * i}>
                    <article data-testid={`credentials-item-${i}`} className="relative pb-16 last:pb-0">
                      <span className="absolute -left-[45px] top-1.5 h-[13px] w-[13px] rounded-full border-2 border-[#0e7c6b] bg-white md:-left-[61px]" />
                      <p className="text-sm font-medium text-[#0e7c6b]">{it.label}</p>
                      <h3 className="font-display mt-3 text-2xl font-light leading-snug tracking-tight text-[#050505] md:text-3xl">{it.title}</h3>
                      <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-[#6b6f6c]">{it.text}</p>
                    </article>
                  </Reveal>
                ))}
              </div>

              {/* Numbers */}
              <Reveal delay={0.15}>
                <div className="mt-20 grid gap-x-8 gap-y-10 border-t border-black/10 pt-12 sm:grid-cols-2 lg:grid-cols-4">
                  {ev.numbers.items.map((n, i) => (
                    <div key={i} data-testid={`credentials-number-${i}`}>
                      <p className="font-display text-4xl font-extralight tracking-tight text-[#050505] lg:text-5xl">{n.v}</p>
                      <p className="mt-2 max-w-[200px] text-sm font-light leading-relaxed text-[#8a8d88]">{n.l}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-8 text-xs font-light text-[#b0b3ae]">{ev.numbers.note}</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section data-testid="home-events-cta-band" className="relative overflow-hidden">
        <img src="/events/formats.jpg" alt={ev.alt.formats} loading="lazy" width="1600" height="1074"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 mx-auto flex min-h-[70vh] max-w-[1400px] flex-col items-center justify-center px-6 py-28 text-center md:px-12">
          <Reveal>
            <h2 className="font-display mx-auto max-w-4xl text-3xl font-light leading-[1.05] tracking-tightest text-white md:text-6xl">{hs.cta.title}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-white/75 md:text-lg">{hs.cta.text}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-11 flex flex-col gap-4 sm:flex-row">
              <Link to={routePath("events", lang)} data-testid="cta-band-events"
                onClick={() => track(EVENTS.CTA_CLICK, { cta: "home_cta_band_events" })}
                className="ep-btn ep-btn-light">
                {hs.cta.ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link to={routePath("contact", lang)} data-testid="cta-band-talk"
                onClick={() => track(EVENTS.CTA_CLICK, { cta: "home_cta_band_talk" })}
                className="ep-btn ep-btn-glass">
                {hs.cta.ctaSecondary}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
